import { openapi } from "../../../../openapi";
import type {
  GetProjectByProjectIdChecklistAndChecklistIdPartsApiResponse,
  GetProjectByProjectIdChecklistAndChecklistIdPartsApiArg,
} from "../../../../openapi";
import { normalizeId } from "@mcapps/shared/sharedUtils";
import { setShowFailedMessageThunk, setShowSuccessMessageThunk } from "../../../global/globalSlice";
import type { AppDispatch } from "../../../store";
import type { IChecklistParts } from "@mctypes/checklist.types";
import { sortArrayByNumber } from "../../utils";
import {
  setChecklistCategoriesInRedux,
  setChecklistItemsInRedux,
  setCurrentChecklistInRedux,
  updateChecklistProgressInRedux,
} from "./checklistSlice";

type ChecklistPartsDraft = GetProjectByProjectIdChecklistAndChecklistIdPartsApiResponse;
type ChecklistPartsArg = GetProjectByProjectIdChecklistAndChecklistIdPartsApiArg;
type ChecklistItemWithProgress = NonNullable<ChecklistPartsDraft["items"]>[number] & {
  progress?: NonNullable<ChecklistPartsDraft["checklist"]>["progress"];
};

const getChecklistPartsArg = (arg: ChecklistPartsArg): ChecklistPartsArg => ({
  projectId: arg.projectId,
  checklistId: arg.checklistId,
});

const patchChecklistParts = (
  dispatch: AppDispatch,
  arg: ChecklistPartsArg,
  recipe: (draft: ChecklistPartsDraft) => void,
) => {
  return dispatch(
    openapi.util.updateQueryData(
      "getProjectByProjectIdChecklistAndChecklistIdParts",
      getChecklistPartsArg(arg),
      recipe,
    ),
  );
};

const setPathValue = (target: Record<string, unknown>, path: string, value: unknown) => {
  const pathParts = path.replace(/^category\./, "").split(".");
  let current = target;

  pathParts.slice(0, -1).forEach((pathPart) => {
    const child = current[pathPart];
    if (child == null || typeof child !== "object") {
      current[pathPart] = {};
    }
    current = current[pathPart] as Record<string, unknown>;
  });

  current[pathParts[pathParts.length - 1]] = value;
};

const checklistTag = (id: string) => ({ type: "Checklist" as const, id });
const partsTag = (arg: ChecklistPartsArg) => checklistTag(`parts:${arg.projectId}:${arg.checklistId}`);
const singleTag = (id: string) => checklistTag(`single:${id}`);
const detailTag = (id: string) => checklistTag(`detail:${id}`);
const projectListsTag = (projectId: string) => checklistTag(`lists:${projectId}`);
const createdChecklistTag = (projectId: string, templatesOnly = false) =>
  checklistTag(`${templatesOnly ? "created-template" : "created-checklist"}:${projectId}`);
const listTags = (data: { _id?: string }[] | undefined, projectId?: string) => [
  projectId ? projectListsTag(projectId) : checklistTag("user-lists"),
  ...(data ?? []).map((item) => checklistTag(`list-item:${item._id}`)),
];
const changedLists = (
  arg: { projectId: string; checklistId?: string; body?: { attribute?: string; templateType?: string } },
  result?: { templateType?: string } | null,
) => [
  projectListsTag(arg.projectId),
  checklistTag("user-lists"),
  // Shared MC templates can enter filtered lists in other projects.
  ...(result?.templateType === "MC" || arg.body?.templateType === "MC" || arg.body?.attribute === "templateType"
    ? [checklistTag("shared-templates")]
    : []),
  // Empty review lists also need to discover newly connected checklists.
  checklistTag("review-lists"),
  ...(arg.checklistId ? [checklistTag(`list-item:${arg.checklistId}`)] : []),
];

openapi.enhanceEndpoints({
  endpoints: {
    postProjectByProjectIdChecklistAndChecklistIdOtherAttributesConfigAdd: {
      invalidatesTags: (_result, error, arg) => (error ? [] : [detailTag(arg.checklistId)]),
    },
    postProjectByProjectIdChecklistAndChecklistIdOtherAttributesConfigUpdate: {
      invalidatesTags: (_data, error, arg) => (error ? [] : [singleTag(arg.checklistId)]),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patch = patchChecklistParts(dispatch, arg, (draft) => {
          if (draft.checklist?.otherAttributesConfig) {
            draft.checklist.otherAttributesConfig[arg.body.otherAttributeConfigKey] =
              arg.body.otherAttributeConfigItemProps;
          }
        });
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
          dispatch(openapi.util.invalidateTags([partsTag(arg)]));
          dispatch(setShowFailedMessageThunk("Feil ved oppdatering av attributtinnstillinger"));
        }
      },
    },
    postProjectByProjectIdChecklistAndChecklistIdOtherAttributesConfigUpdateorder: {
      invalidatesTags: (_data, error, arg) => (error ? [] : [singleTag(arg.checklistId)]),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patch = patchChecklistParts(dispatch, arg, (draft) => {
          arg.body.idsInOrder.forEach((key, index) => {
            const config = draft.checklist?.otherAttributesConfig?.[key];
            if (config) config.order = index;
          });
        });
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
          dispatch(openapi.util.invalidateTags([partsTag(arg)]));
          dispatch(setShowFailedMessageThunk("Feil ved sortering av attributter"));
        }
      },
    },
    postProjectByProjectIdChecklistAll: {
      providesTags: (data, _error, arg) => [
        ...listTags(data, arg.projectId),
        createdChecklistTag(arg.projectId, arg.body.isTemplate === true),
        checklistTag("shared-templates"),
      ],
    },
    getProjectByProjectIdChecklistTemplates: {
      providesTags: (data, _error, arg) => [
        ...listTags(data, arg.projectId),
        createdChecklistTag(arg.projectId, true),
        checklistTag("shared-templates"),
      ],
    },
    getProjectByProjectIdChecklistInstanceAndChecklistId: {
      providesTags: (_data, _error, arg) => [detailTag(arg.checklistId), singleTag(arg.checklistId)],
    },
    getProjectByProjectIdChecklistTags: {
      providesTags: (_data, _error, arg) => [projectListsTag(arg.projectId), createdChecklistTag(arg.projectId)],
    },
    getProjectByProjectIdChecklistCreatedByUser: {
      providesTags: (data, _error, arg) => [...listTags(data, arg.projectId), createdChecklistTag(arg.projectId)],
    },
    getChecklistCreatedByUser: { providesTags: (data) => listTags(data) },
    postProjectByProjectIdChecklistImportExcel: {
      invalidatesTags: (_data, error, arg) => (error ? [] : changedLists(arg, _data)),
    },
    postProjectByProjectIdChecklistAdd: {
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...changedLists(arg, result).filter((tag) => tag.id !== projectListsTag(arg.projectId).id),
              createdChecklistTag(arg.projectId),
              ...(result?.isTemplate === true || arg.body.isTemplate === true
                ? [createdChecklistTag(arg.projectId, true)]
                : []),
            ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(setShowFailedMessageThunk("Feil ved oppretting av sjekkliste"));
        }
      },
    },
    getProjectByProjectIdChecklistAndChecklistIdParts: {
      providesTags: (_result, _error, arg) => [partsTag(arg), detailTag(arg.checklistId)],
    },
    postProjectByProjectIdChecklistUpdateRowPosition: {
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const target = { projectId: arg.projectId, checklistId: String(arg.body.checklistId) };
        try {
          const { data } = await queryFulfilled;
          patchChecklistParts(dispatch, target, (draft) => {
            // The server renumbers rows and returns the affected collection.
            if (arg.body.isCat) draft.categories = data as ChecklistPartsDraft["categories"];
            else draft.items = data as ChecklistPartsDraft["items"];
          });
        } catch {
          dispatch(openapi.util.invalidateTags([partsTag(target)]));
          dispatch(setShowFailedMessageThunk("Feil ved flytting av rad"));
        }
      },
    },
    putProjectByProjectIdChecklistAndChecklistId: {
      invalidatesTags: (_result, error, arg) =>
        error ? [] : [...changedLists(arg, _result), singleTag(arg.checklistId)],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = patchChecklistParts(dispatch, arg, (draft) => {
          if (draft.checklist != null) {
            setPathValue(draft.checklist as Record<string, unknown>, arg.body.attribute, arg.body.newValue);
          }
        });

        try {
          const { data } = await queryFulfilled;
          patchChecklistParts(dispatch, arg, (draft) => {
            draft.checklist = data;
          });
          dispatch(setShowSuccessMessageThunk("Sjekkliste oppdatert"));
        } catch {
          patchResult.undo();
          dispatch(openapi.util.invalidateTags([partsTag(arg)]));
          dispatch(setShowFailedMessageThunk("Feil ved oppdatering av sjekkliste"));
        }
      },
    },
    putProjectByProjectIdChecklistAndChecklistIdUpdateChecklistIds: {
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          patchChecklistParts(dispatch, arg, (draft) => {
            Object.assign(draft, data);
          });
        } catch {
          dispatch(openapi.util.invalidateTags([partsTag(arg)]));
          dispatch(setShowFailedMessageThunk("Feil ved oppdatering av radnummer"));
        }
      },
    },
    postProjectByProjectIdChecklistAndChecklistIdCategory: {
      invalidatesTags: (_result, error, arg) =>
        error ? [] : [{ type: "Checklist", id: `parts:${arg.projectId}:${arg.checklistId}` }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          dispatch(setShowFailedMessageThunk("Feil ved oppretting av sjekklistekategori"));
        }
      },
    },
    postProjectByProjectIdChecklistAndChecklistIdItem: {
      invalidatesTags: (_result, error, arg) =>
        error ? [] : [{ type: "Checklist", id: `parts:${arg.projectId}:${arg.checklistId}` }],
    },
    putProjectByProjectIdChecklistAndChecklistIdCategoryCategoryId: {
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = patchChecklistParts(dispatch, arg, (draft) => {
          const category = draft.categories.find((item) => item._id === arg.categoryId);
          if (category != null) {
            setPathValue(category as Record<string, unknown>, arg.body.attribute, arg.body.newValue);
          }
        });

        try {
          const { data } = await queryFulfilled;
          patchChecklistParts(dispatch, arg, (draft) => {
            const categoryIndex = draft.categories.findIndex((category) => category._id === data._id);
            if (categoryIndex !== -1) {
              draft.categories[categoryIndex] = data;
            }
          });
          dispatch(setShowSuccessMessageThunk("Sjekkliste oppdatert"));
        } catch {
          patchResult.undo();
          dispatch(openapi.util.invalidateTags([partsTag(arg)]));
          dispatch(setShowFailedMessageThunk("Feil ved oppdatering av sjekklistekategori"));
        }
      },
    },
    deleteProjectByProjectIdChecklistAndChecklistIdCategoryCategoryId: {
      invalidatesTags: (_data, error, arg) => (error ? [] : [partsTag(arg)]),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patch = patchChecklistParts(dispatch, arg, (draft) => {
          draft.categories = draft.categories.filter((category) => category._id !== arg.categoryId);
          draft.items = draft.items.filter((item) => normalizeId(item.category) !== arg.categoryId);
        });
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
          dispatch(openapi.util.invalidateTags([partsTag(arg)]));
          dispatch(setShowFailedMessageThunk("Feil ved sletting"));
        }
      },
    },
    putProjectByProjectIdChecklistAndChecklistIdCategoryCategoryIdInactive: {
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = patchChecklistParts(dispatch, arg, (draft) => {
          draft.items.forEach((item) => {
            if (normalizeId(item.category) === arg.categoryId) {
              item.inactive = {
                ...item.inactive,
                isChecked: arg.body.inactive,
              };
            }
          });
        });

        try {
          const { data } = await queryFulfilled;
          patchChecklistParts(dispatch, arg, (draft) => {
            Object.assign(draft, data);
          });
          if (data.checklist != null) {
            // The legacy checklist still stores its pre-OpenAPI view types in Redux.
            const legacyParts = data as unknown as IChecklistParts;
            dispatch(updateChecklistProgressInRedux(legacyParts.checklist.progress));
            dispatch(setCurrentChecklistInRedux(legacyParts.checklist));
            dispatch(setChecklistItemsInRedux(sortArrayByNumber(legacyParts.items, "itemId")));
            dispatch(setChecklistCategoriesInRedux(sortArrayByNumber(legacyParts.categories, "categoryId")));
          }
          dispatch(setShowSuccessMessageThunk("Sjekkliste oppdatert"));
        } catch {
          patchResult.undo();
          dispatch(openapi.util.invalidateTags([partsTag(arg)]));
          dispatch(setShowFailedMessageThunk("Feil ved oppdatering av kategori"));
        }
      },
    },
    deleteProjectByProjectIdChecklistAndChecklistIdItemItemId: {
      invalidatesTags: (_data, error, arg) => (error ? [] : [partsTag(arg)]),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patch = patchChecklistParts(dispatch, arg, (draft) => {
          draft.items = draft.items.filter((item) => item._id !== arg.itemId);
        });
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
          dispatch(openapi.util.invalidateTags([partsTag(arg)]));
          dispatch(setShowFailedMessageThunk("Feil ved sletting"));
        }
      },
    },
    putProjectByProjectIdChecklistAndChecklistIdItemItemIdOtherAttribute: {
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patch = patchChecklistParts(dispatch, arg, (draft) => {
          const item = draft.items.find((item) => item._id === arg.itemId);
          if (item) {
            item.otherAttributesData ??= {};
            item.otherAttributesData[arg.body.attributeKey] = arg.body.newValue;
          }
        });
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
          dispatch(openapi.util.invalidateTags([partsTag(arg)]));
          dispatch(setShowFailedMessageThunk("Feil ved oppdatering av attributt"));
        }
      },
    },
    putProjectByProjectIdChecklistAndChecklistIdItemItemId: {
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = patchChecklistParts(dispatch, arg, (draft) => {
          const item = draft.items.find((item) => item._id === arg.itemId);
          if (item != null) {
            setPathValue(item as Record<string, unknown>, arg.body.path, arg.body.newValue);
          }
        });

        try {
          const { data } = await queryFulfilled;
          patchChecklistParts(dispatch, arg, (draft) => {
            const { progress, ...updatedItem } = data as ChecklistItemWithProgress;
            const itemIndex = draft.items.findIndex((item) => item._id === data._id);

            if (itemIndex !== -1) {
              draft.items[itemIndex] = updatedItem;
            }

            if (draft.checklist != null && progress != null) {
              draft.checklist.progress = progress;
            }
          });
          dispatch(setShowSuccessMessageThunk("Sjekkliste oppdatert"));
        } catch {
          patchResult.undo();
          dispatch(openapi.util.invalidateTags([partsTag(arg)]));
          dispatch(setShowFailedMessageThunk("Feil ved oppdatering av sjekklisterad"));
        }
      },
    },
    getChecklistsByReviewByReviewId: {
      providesTags: (data) => [
        checklistTag("review-lists"),
        ...(data ?? []).map((item) => checklistTag(`list-item:${item._id}`)),
      ],
    },
    putChecklistsByChecklistIdDkApprovalToggle: {
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            openapi.util.invalidateTags([detailTag(arg.checklistId), checklistTag(`list-item:${arg.checklistId}`)]),
          );
        } catch {
          // no-op: leave cache untouched on failed toggle
        }
      },
    },
  },
});
